import { useState, useEffect, type FormEvent } from 'react';

declare global {
  interface Window { gtag?: (...args: unknown[]) => void; }
}
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useAuthStore } from '@/stores/useAuthStore';
import { AuthLayout } from '@/components/layout/AuthLayout';
import { SsoButtons } from '@/components/auth/SsoButtons';
import { CompanyLogoOrInitials } from '@/components/ui/CompanyLogoOrInitials';
import { useT } from '@/i18n/LanguageContext';

interface PublicCompanyData {
  id: string;
  companyName: string;
  hasLogo: boolean;
  sources: string[];
}

async function fetchPublicCompany(id: string): Promise<PublicCompanyData> {
  const res = await fetch(`/api/public/company/${id}`);
  if (!res.ok) throw new Error('Not found');
  return res.json();
}

export default function SignUpPage() {
  const navigate = useNavigate();
  const signup = useAuthStore((s) => s.signup);
  const t = useT();
  const [searchParams] = useSearchParams();
  const atlasId = searchParams.get('id');
  const ref = searchParams.get('ref');

  const { data: company } = useQuery<PublicCompanyData>({
    queryKey: ['public-company', atlasId],
    queryFn: () => fetchPublicCompany(atlasId!),
    enabled: !!atlasId,
    staleTime: 5 * 60 * 1000,
    retry: 1,
  });

  const [companyName, setCompanyName] = useState('');

  // Pre-fill company name once Atlas data loads (only if user hasn't typed yet)
  useEffect(() => {
    if (company?.companyName && !companyName) {
      setCompanyName(company.companyName);
    }
  }, [company]);
  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [wechat, setWechat] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError('');
    if (!companyName.trim() || !fullName.trim() || !email.trim() || !phone.trim() || !password.trim()) {
      setError(t.signup.allFieldsRequired);
      return;
    }
    if (password.length < 8) {
      setError(t.signup.passwordMinLength);
      return;
    }
    setLoading(true);
    const result = await signup(
      companyName.trim(),
      fullName.trim(),
      email.trim(),
      password.trim(),
      phone.trim(),
      wechat.trim() || undefined,
      ref || undefined,
    );
    setLoading(false);
    if (result === true) {
      window.gtag?.('event', 'generate_lead', {
        event_category: 'registration',
        event_label: companyName.trim(),
      });
      navigate('/', { replace: true });
    } else {
      setError(result);
    }
  };

  return (
    <AuthLayout>
      {/* Personalized company banner — shown when arriving from an outreach LP link */}
      {company && (
        <div className="flex items-center gap-3 mb-6 px-4 py-3 bg-white border border-[#DDDDDD] rounded-2xl shadow-sm max-w-sm mx-auto">
          <CompanyLogoOrInitials company={company} size="sm" />
          <div className="text-left">
            <p className="text-[10px] font-semibold uppercase tracking-[0.15em] text-[#9A9A9A]">
              Creating account for
            </p>
            <p className="text-sm font-semibold text-[#222222] leading-tight">{company.companyName}</p>
          </div>
        </div>
      )}

      {/* Label */}
      <p className="text-sm font-semibold uppercase tracking-[0.2em] text-[#523EF7] mb-4">
        {t.common.appName}
      </p>

      <h1 className="text-3xl sm:text-4xl font-bold text-[#222222] tracking-tight leading-tight mb-2">
        {t.signup.title}
      </h1>
      <p className="text-[#9A9A9A] text-base mb-8">
        {t.signup.subtitle}
      </p>

      {/* SSO Buttons + Form */}
      <div className="max-w-sm mx-auto">
        {/* SSO Buttons */}
        <SsoButtons onError={(msg) => setError(msg)} />

        <form onSubmit={handleSubmit} className="space-y-4 text-left">
          <div className="flex flex-col gap-1">
            <label htmlFor="company-name" className="text-sm font-medium text-[#3F3F3F]">
              {t.signup.companyName} <span className="text-red-500">*</span>
            </label>
            <input
              id="company-name"
              placeholder={t.signup.companyPlaceholder}
              value={companyName}
              onChange={(e) => setCompanyName(e.target.value)}
              required
              autoFocus
              className="auth-input"
            />
          </div>
          <div className="flex flex-col gap-1">
            <label htmlFor="full-name" className="text-sm font-medium text-[#3F3F3F]">
              {t.signup.fullName} <span className="text-red-500">*</span>
            </label>
            <input
              id="full-name"
              placeholder={t.signup.fullNamePlaceholder}
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
              required
              className="auth-input"
            />
          </div>
          <div className="flex flex-col gap-1">
            <label htmlFor="email" className="text-sm font-medium text-[#3F3F3F]">
              {t.common.email} <span className="text-red-500">*</span>
            </label>
            <input
              id="email"
              type="email"
              placeholder={t.signup.emailPlaceholder}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              className="auth-input"
            />
          </div>
          <div className="flex flex-col gap-1">
            <label htmlFor="phone" className="text-sm font-medium text-[#3F3F3F]">
              {t.common.phone} <span className="text-red-500">*</span>
            </label>
            <input
              id="phone"
              type="tel"
              placeholder={t.signup.phonePlaceholder}
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              required
              className="auth-input"
            />
          </div>
          <div className="flex flex-col gap-1">
            <label htmlFor="wechat" className="text-sm font-medium text-[#3F3F3F]">
              {t.signup.wechatLabel}
            </label>
            <input
              id="wechat"
              placeholder={t.signup.wechatPlaceholder}
              value={wechat}
              onChange={(e) => setWechat(e.target.value)}
              className="auth-input"
            />
          </div>
          <div className="flex flex-col gap-1">
            <label htmlFor="password" className="text-sm font-medium text-[#3F3F3F]">
              {t.common.password} <span className="text-red-500">*</span>
            </label>
            <input
              id="password"
              type="password"
              placeholder={t.signup.passwordPlaceholder}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              className="auth-input"
            />
          </div>

          {error && (
            <p className="text-sm text-red-600 bg-red-50 rounded-xl px-3 py-2">{error}</p>
          )}

          <button
            type="submit"
            disabled={loading}
            className="auth-btn-primary w-full"
          >
            {loading ? (
              <svg className="animate-spin h-5 w-5 mx-auto" viewBox="0 0 24 24" fill="none">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
              </svg>
            ) : (
              t.signup.submit
            )}
          </button>

          <div className="text-center pt-1">
            <Link
              to="/login"
              className="text-sm text-[#9A9A9A] hover:text-[#523EF7] transition-colors"
            >
              {t.signup.hasAccount}
            </Link>
          </div>
        </form>
      </div>
    </AuthLayout>
  );
}
